/**
 * ASKS - one seat asking another for an answer.
 *
 * "Who does this blast catch?", and later "roll Scrutiny for me": a request
 * a client cannot settle alone, written down so the seat it is addressed to
 * can see it, answer it, and the asker can act on the answer. One document
 * per ask (`asks/<id>`), and one direction of travel:
 *
 *   open -> seen -> answered
 *      \\_______\\__> cancelled
 *
 * Every transition is a pure function that returns the next ask, or NULL
 * when it is not allowed from here - which is what makes two people
 * answering at once safe: the transaction applies the transition to the
 * stored ask, and the second one finds it already answered.
 *
 * Pure, zero React dependency. The payload and the answer are each kind's
 * own business; this file never looks inside them.
 */

const ASK_STATUSES = ['open', 'seen', 'answered', 'cancelled'] as const;
type AskStatus = (typeof ASK_STATUSES)[number];

/** Who an ask is for: the GM's seat, or one account. */
export type AskAddressee = 'gm' | { uid: string };

export interface Ask<P = unknown, A = unknown> {
  id: string;
  /** What is being asked - each kind has one reader. */
  kind: string;
  /** The asker's account, and the name to show beside the ask. */
  from: { uid: string; name: string };
  to: AskAddressee;
  payload: P;
  status: AskStatus;
  /** Present exactly when `status` is 'answered'. */
  answer?: A;
  /** Milliseconds; the asker's clock. Orders the tray, nothing else. */
  createdAt: number;
}

interface NewAsk<P> {
  id: string;
  kind: string;
  from: Ask['from'];
  to: AskAddressee;
  payload: P;
  createdAt: number;
}

export function openAsk<P>(fields: NewAsk<P>): Ask<P> {
  return { ...fields, status: 'open' };
}

/** Still waiting for its answer? */
export const isPending = (ask: Pick<Ask, 'status'>): boolean => ask.status === 'open' || ask.status === 'seen';

/** The addressee has it on screen. Only an open ask can become seen. */
export function seeAsk<P, A>(ask: Ask<P, A>): Ask<P, A> | null {
  return ask.status === 'open' ? { ...ask, status: 'seen' } : null;
}

/** The answer. Only a pending ask can be answered: the first answer stands. */
export function answerAsk<P, A>(ask: Ask<P, A>, answer: A): Ask<P, A> | null {
  return isPending(ask) ? { ...ask, status: 'answered', answer } : null;
}

/** Withdrawn by the asker, or dismissed by the addressee. An answered ask stays answered. */
export function cancelAsk<P, A>(ask: Ask<P, A>): Ask<P, A> | null {
  return isPending(ask) ? { ...ask, status: 'cancelled' } : null;
}

/** Is this ask addressed to this seat? */
export function isAddressedTo(ask: Pick<Ask, 'to'>, seat: { uid: string | null; isGm: boolean }): boolean {
  if (ask.to === 'gm') return seat.isGm;
  return !!seat.uid && ask.to.uid === seat.uid;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function readAddressee(raw: unknown): AskAddressee | null {
  if (raw === 'gm') return 'gm';
  return isRecord(raw) && typeof raw.uid === 'string' && raw.uid ? { uid: raw.uid } : null;
}

function readFrom(raw: unknown): Ask['from'] | null {
  if (!isRecord(raw) || typeof raw.uid !== 'string' || !raw.uid) return null;
  return { uid: raw.uid, name: typeof raw.name === 'string' ? raw.name : '' };
}

/**
 * A stored ask, validated: anything this build cannot read - an unknown
 * status, no addressee, no asker - is NULL and is simply not shown, rather
 * than half-trusted. The payload is handed on untouched for its kind to read.
 */
export function readAsk(id: string, raw: unknown): Ask | null {
  if (!isRecord(raw)) return null;
  const to = readAddressee(raw.to);
  const from = readFrom(raw.from);
  const status = ASK_STATUSES.find((s) => s === raw.status);
  if (!to || !from || !status || typeof raw.kind !== 'string' || !raw.kind) return null;
  return {
    id,
    kind: raw.kind,
    from,
    to,
    payload: raw.payload,
    status,
    ...(status === 'answered' ? { answer: raw.answer } : {}),
    createdAt: typeof raw.createdAt === 'number' ? raw.createdAt : 0,
  };
}
